import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { ateOuIgual, dataValida, MENSAGEM_DATA, noPassado } from '@/lib/datas';
import { mascaraTelefone, validarTelefone, MENSAGEM } from '@/lib/mascaras';

interface Conselheiro {
  id: string;
  condominio_id: string;
  nome: string;
  cargo: string;
  telefone?: string;
  email?: string;
  data_inicio_mandato: string;
  data_fim_mandato?: string;
  ativo: boolean;
  observacoes?: string;
}

const EMPTY_CONSELHEIRO: Omit<Conselheiro, 'id' | 'condominio_id'> = {
  nome: '',
  cargo: 'Conselheiro',
  telefone: '',
  email: '',
  data_inicio_mandato: '',
  data_fim_mandato: '',
  ativo: true,
  observacoes: '',
};

function formatDate(iso: string): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function formatDateToInput(iso: string): string {
  if (!iso) return '';
  return iso.substring(0, 10);
}

/**
 * Regras de mandato, iguais no cadastro e na edicao.
 *
 * O espelho no banco esta na migration 0014: a constraint recusa fim antes do
 * inicio, e o gatilho manda para o historico quem ja venceu. Aqui a checagem
 * existe para explicar o erro antes de gravar.
 */
function validarMandato(inicio: string, fim: string | undefined | null): string | null {
  if (!inicio.trim()) return 'A data de início do mandato é obrigatória.';
  if (!dataValida(inicio)) return `Início do mandato: ${MENSAGEM_DATA.invalida}`;

  const fimInformado = (fim || '').trim();
  if (!fimInformado) return null;

  if (!dataValida(fimInformado)) return `Fim do mandato: ${MENSAGEM_DATA.invalida}`;
  if (!ateOuIgual(inicio, fimInformado)) return MENSAGEM_DATA.fimAntesDoInicio;
  return null;
}

/**
 * Telefone opcional, mas válido quando informado.
 *
 * O resto do produto já mascara e valida telefone por `lib/mascaras`; esta aba
 * era a única que aceitava qualquer texto (P-36), e o número guardado assim não
 * serve para ligar nem para exportar.
 */
function validarTelefoneOpcional(telefone: string | undefined | null): string | null {
  const informado = (telefone || '').trim();
  if (!informado) return null;
  return validarTelefone(informado) ? null : MENSAGEM.telefone;
}

/** Mandato encerrado no passado entra direto no historico. */
const mandatoVigente = (fim: string | undefined | null): boolean => !noPassado(fim || '');

export default function ConselheirosTab() {
  const { id: condoId } = useParams<{ id: string }>();
  const { effectiveAccountId } = useAuth();

  const [conselheiros, setConselheiros] = useState<Conselheiro[]>([]);
  const [historial, setHistorial] = useState<Conselheiro[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);
  const [mostrarFormNovo, setMostrarFormNovo] = useState(false);

  // Editing state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<Conselheiro>>({});

  // New conselheiro form
  const [novoConselheiro, setNovoConselheiro] = useState({ ...EMPTY_CONSELHEIRO });

  const fetchConselheiros = useCallback(async () => {
    if (!condoId) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: fErr } = await supabase
        .from('conselheiros')
        .select('*')
        .eq('condominio_id', condoId)
        .order('data_inicio_mandato', { ascending: false });

      if (fErr) throw new Error(fErr.message);

      // A lista vem do banco. Sem fallback para mock: conselho que aparece na
      // tela e conselho que existe no cadastro — o contrario ja fez a exclusao
      // parecer que nao funcionava, com o registro "apagado" voltando do mock.
      const registros = (data || []) as Conselheiro[];
      setConselheiros(registros.filter((c) => c.ativo));
      setHistorial(registros.filter((c) => !c.ativo));
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar conselheiros.');
      setConselheiros([]);
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  }, [condoId]);

  useEffect(() => {
    void fetchConselheiros();
  }, [fetchConselheiros]);

  const startEdit = (c: Conselheiro) => {
    setEditingId(c.id);
    setEditData({ ...c });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (id: string) => {
    if (!condoId || !effectiveAccountId) return;

    const erroDeData = validarMandato(
      editData.data_inicio_mandato || '',
      editData.data_fim_mandato,
    );
    if (erroDeData) {
      setError(erroDeData);
      return;
    }

    const erroDeTelefone = validarTelefoneOpcional(editData.telefone);
    if (erroDeTelefone) {
      setError(erroDeTelefone);
      return;
    }

    setError(null);
    setSaving(true);
    try {
      const { error: uErr } = await supabase
        .from('conselheiros')
        .update({
          nome: editData.nome,
          cargo: editData.cargo,
          telefone: editData.telefone || null,
          email: editData.email || null,
          data_inicio_mandato: editData.data_inicio_mandato,
          data_fim_mandato: editData.data_fim_mandato || null,
          observacoes: editData.observacoes || null,
        })
        .eq('id', id);

      if (uErr) throw new Error(uErr.message);

      setEditingId(null);
      setEditData({});
      fetchConselheiros();
    } catch (e: any) {
      setError(e.message || 'Erro ao salvar alterações.');
    } finally {
      setSaving(false);
    }
  };

  const deleteConselheiro = async (id: string) => {
    if (!condoId) return;
    if (!window.confirm('Tem certeza que deseja remover este conselheiro?')) return;

    setSaving(true);
    try {
      const { error: dErr } = await supabase.from('conselheiros').delete().eq('id', id);

      if (dErr) throw new Error(dErr.message);
      fetchConselheiros();
    } catch (e: any) {
      setError(e.message || 'Erro ao remover conselheiro.');
    } finally {
      setSaving(false);
    }
  };

  const addConselheiro = async () => {
    if (!condoId || !effectiveAccountId) return;
    if (!novoConselheiro.nome.trim()) {
      setError('O nome do conselheiro é obrigatório.');
      return;
    }

    const erroDeData = validarMandato(
      novoConselheiro.data_inicio_mandato,
      novoConselheiro.data_fim_mandato,
    );
    if (erroDeData) {
      setError(erroDeData);
      return;
    }

    const erroDeTelefone = validarTelefoneOpcional(novoConselheiro.telefone);
    if (erroDeTelefone) {
      setError(erroDeTelefone);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const { error: iErr } = await supabase.from('conselheiros').insert({
        condominio_id: condoId,
        account_id: effectiveAccountId,
        nome: novoConselheiro.nome,
        cargo: novoConselheiro.cargo,
        telefone: novoConselheiro.telefone || null,
        email: novoConselheiro.email || null,
        data_inicio_mandato: novoConselheiro.data_inicio_mandato,
        data_fim_mandato: novoConselheiro.data_fim_mandato || null,
        // Mandato ja encerrado nasce no historico, nao como conselheiro ativo.
        ativo: mandatoVigente(novoConselheiro.data_fim_mandato),
        observacoes: novoConselheiro.observacoes || null,
      });

      if (iErr) throw new Error(iErr.message);

      setNovoConselheiro({ ...EMPTY_CONSELHEIRO });
      setMostrarFormNovo(false);
      fetchConselheiros();
    } catch (e: any) {
      setError(e.message || 'Erro ao adicionar conselheiro.');
    } finally {
      setSaving(false);
    }
  };

  const encerrarMandato = async (id: string) => {
    if (!condoId) return;
    const hoje = new Date().toISOString().substring(0, 10);
    if (
      !window.confirm(
        `Encerrar o mandato deste conselheiro em ${formatDate(hoje)}? Ele será movido para o histórico.`,
      )
    )
      return;

    setSaving(true);
    try {
      const { error: uErr } = await supabase
        .from('conselheiros')
        .update({
          ativo: false,
          data_fim_mandato: hoje,
        })
        .eq('id', id);

      if (uErr) throw new Error(uErr.message);
      fetchConselheiros();
    } catch (e: any) {
      setError(e.message || 'Erro ao encerrar mandato.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Carregando conselheiros...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Error toast */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className="ri-error-warning-line text-red-500"></i>
          </div>
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto w-4 h-4 flex items-center justify-center text-red-400 hover:text-red-600"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}

      {/* Conselho Atual */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-accent-100 flex items-center justify-center">
              <i className="ri-group-line text-accent-700 text-sm"></i>
            </div>
            <h3 className="text-base font-semibold text-foreground-900">Conselho Atual</h3>
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-50 text-accent-700 border border-accent-200">
              {conselheiros.length} membro{conselheiros.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={() => setMostrarFormNovo(!mostrarFormNovo)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            Adicionar Conselheiro
          </button>
        </div>

        {/* Form novo conselheiro */}
        {mostrarFormNovo && (
          <div className="mb-5 p-4 rounded-lg border border-accent-300/60 bg-accent-50/50">
            <h4 className="text-sm font-semibold text-foreground-900 mb-4">Novo Conselheiro</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Nome completo *
                </label>
                <input
                  type="text"
                  value={novoConselheiro.nome}
                  onChange={(e) => setNovoConselheiro({ ...novoConselheiro, nome: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  placeholder="Nome do conselheiro"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Cargo
                </label>
                <select
                  value={novoConselheiro.cargo}
                  onChange={(e) =>
                    setNovoConselheiro({ ...novoConselheiro, cargo: e.target.value })
                  }
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                >
                  <option value="Presidente do Conselho">Presidente do Conselho</option>
                  <option value="Vice-Presidente do Conselho">Vice-Presidente do Conselho</option>
                  <option value="Conselheiro Fiscal">Conselheiro Fiscal</option>
                  <option value="Conselheiro">Conselheiro</option>
                  <option value="Secretário">Secretário</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Telefone
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={15}
                  value={novoConselheiro.telefone}
                  onChange={(e) =>
                    setNovoConselheiro({
                      ...novoConselheiro,
                      telefone: mascaraTelefone(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  placeholder="(11) 99999-9999"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  E-mail
                </label>
                <input
                  type="email"
                  value={novoConselheiro.email}
                  onChange={(e) =>
                    setNovoConselheiro({ ...novoConselheiro, email: e.target.value })
                  }
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Início do Mandato *
                </label>
                <input
                  type="date"
                  value={novoConselheiro.data_inicio_mandato}
                  onChange={(e) =>
                    setNovoConselheiro({ ...novoConselheiro, data_inicio_mandato: e.target.value })
                  }
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Fim do Mandato
                  <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
                </label>
                <input
                  type="date"
                  value={novoConselheiro.data_fim_mandato}
                  onChange={(e) =>
                    setNovoConselheiro({ ...novoConselheiro, data_fim_mandato: e.target.value })
                  }
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Observações
                  <span className="text-foreground-400 font-normal ml-1">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={novoConselheiro.observacoes}
                  onChange={(e) =>
                    setNovoConselheiro({ ...novoConselheiro, observacoes: e.target.value })
                  }
                  className="w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm"
                  placeholder="Ex: Eleito em assembleia..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-background-200/70">
              <button
                onClick={() => {
                  setMostrarFormNovo(false);
                  setNovoConselheiro({ ...EMPTY_CONSELHEIRO });
                  setError(null);
                }}
                className="px-4 py-2 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={addConselheiro}
                disabled={saving}
                className="px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Adicionar'}
              </button>
            </div>
          </div>
        )}

        {conselheiros.length === 0 && !mostrarFormNovo ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-group-line text-foreground-400 text-xl"></i>
            </div>
            <p className="text-sm text-foreground-500">Nenhum conselheiro cadastrado.</p>
            <p className="text-xs text-foreground-400">
              Clique em "Adicionar Conselheiro" para começar.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {conselheiros.map((c) => (
              <div
                key={c.id}
                className="border border-background-200/70 rounded-lg p-4 bg-background-50 hover:border-background-300/80 transition-colors"
              >
                {editingId === c.id ? (
                  <>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Nome
                        </label>
                        <input
                          type="text"
                          value={editData.nome || ''}
                          onChange={(e) => setEditData({ ...editData, nome: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Cargo
                        </label>
                        <select
                          value={editData.cargo || 'Conselheiro'}
                          onChange={(e) => setEditData({ ...editData, cargo: e.target.value })}
                          className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                        >
                          <option value="Presidente do Conselho">Presidente do Conselho</option>
                          <option value="Vice-Presidente do Conselho">
                            Vice-Presidente do Conselho
                          </option>
                          <option value="Conselheiro Fiscal">Conselheiro Fiscal</option>
                          <option value="Conselheiro">Conselheiro</option>
                          <option value="Secretário">Secretário</option>
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            Telefone
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={15}
                            placeholder="(11) 99999-9999"
                            value={mascaraTelefone(editData.telefone || '')}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                telefone: mascaraTelefone(e.target.value),
                              })
                            }
                            className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            E-mail
                          </label>
                          <input
                            type="email"
                            value={editData.email || ''}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                            className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            Início do Mandato
                          </label>
                          <input
                            type="date"
                            value={formatDateToInput(editData.data_inicio_mandato || '')}
                            onChange={(e) =>
                              setEditData({ ...editData, data_inicio_mandato: e.target.value })
                            }
                            className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-foreground-600 mb-1">
                            Fim do Mandato
                          </label>
                          <input
                            type="date"
                            value={formatDateToInput(editData.data_fim_mandato || '')}
                            onChange={(e) =>
                              setEditData({ ...editData, data_fim_mandato: e.target.value || '' })
                            }
                            className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">
                          Observações
                        </label>
                        <input
                          type="text"
                          value={editData.observacoes || ''}
                          onChange={(e) =>
                            setEditData({ ...editData, observacoes: e.target.value })
                          }
                          className="w-full px-3 py-2 rounded-md border border-background-300/60 bg-background-50 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-background-200/60">
                      <button
                        onClick={cancelEdit}
                        className="px-3 py-1.5 rounded-md border border-background-300/60 text-xs text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => saveEdit(c.id)}
                        disabled={saving}
                        className="px-3 py-1.5 rounded-md bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-50"
                      >
                        {saving ? 'Salvando...' : 'Salvar'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-foreground-900">{c.nome}</h4>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-accent-50 text-accent-700 border border-accent-200">
                            {c.cargo}
                          </span>
                        </div>
                        {c.observacoes && (
                          <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                            {c.observacoes}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => startEdit(c)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors"
                          title="Editar"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => encerrarMandato(c.id)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                          title="Encerrar mandato"
                        >
                          <i className="ri-calendar-close-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => deleteConselheiro(c.id)}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Remover"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5">
                      {c.telefone && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
                          <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                            <i className="ri-phone-line text-xs"></i>
                          </div>
                          {mascaraTelefone(c.telefone)}
                        </div>
                      )}
                      {c.email && (
                        <div className="flex items-center gap-1.5 text-xs text-foreground-600">
                          <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400">
                            <i className="ri-mail-line text-xs"></i>
                          </div>
                          {c.email}
                        </div>
                      )}
                    </div>

                    <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                      <div className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-calendar-line text-xs"></i>
                        </div>
                        <span>{formatDate(c.data_inicio_mandato)}</span>
                      </div>
                      <span>—</span>
                      <div className="flex items-center gap-1">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-calendar-check-line text-xs"></i>
                        </div>
                        <span>
                          {c.data_fim_mandato ? formatDate(c.data_fim_mandato) : 'Em aberto'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Histórico */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <button
          onClick={() => setMostrarHistorico(!mostrarHistorico)}
          className="inline-flex items-center gap-2 text-sm font-medium text-foreground-700 hover:text-foreground-900 transition-colors"
        >
          <div
            className={`w-4 h-4 flex items-center justify-center transition-transform ${mostrarHistorico ? 'rotate-90' : ''}`}
          >
            <i className="ri-arrow-right-s-line"></i>
          </div>
          Histórico de Conselheiros
          <span className="text-xs text-foreground-400">({historial.length})</span>
        </button>

        {mostrarHistorico && (
          <div className="mt-5">
            {historial.length === 0 ? (
              <p className="text-sm text-foreground-500 py-4 text-center">
                Nenhum conselheiro no histórico.
              </p>
            ) : (
              <div className="relative">
                <div className="absolute left-[11px] top-3 bottom-3 w-px bg-background-200/70"></div>
                <div className="flex flex-col gap-4 pl-2">
                  {historial.map((c, idx) => (
                    <div key={c.id} className="flex items-start gap-3 relative">
                      <div
                        className={`relative z-10 mt-1 w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 ${
                          idx === historial.length - 1
                            ? 'bg-background-50 border-background-300'
                            : 'bg-foreground-300 border-foreground-300'
                        }`}
                      ></div>
                      <div className="flex-1 min-w-0 bg-background-100/60 rounded-lg p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-foreground-800">{c.nome}</p>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-foreground-100 text-foreground-600">
                            {c.cargo}
                          </span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-background-200/70 text-foreground-500">
                            Mandato encerrado
                          </span>
                        </div>

                        {/* A ficha inteira, e não só nome e telefone (P-37): quem
                            consulta o histórico está procurando justamente o dado de
                            contato ou o período exato de quem assinou alguma coisa. */}
                        <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <dt className="sr-only">Início do mandato</dt>
                            <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400 shrink-0">
                              <i className="ri-calendar-line text-[11px]"></i>
                            </div>
                            <dd className="text-xs text-foreground-600 truncate">
                              {formatDate(c.data_inicio_mandato)} —{' '}
                              {c.data_fim_mandato ? formatDate(c.data_fim_mandato) : 'sem término'}
                            </dd>
                          </div>

                          <div className="flex items-center gap-1.5 min-w-0">
                            <dt className="sr-only">Telefone</dt>
                            <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400 shrink-0">
                              <i className="ri-phone-line text-[11px]"></i>
                            </div>
                            <dd className="text-xs text-foreground-600 truncate">
                              {/* O cadastro antigo guardou o numero sem pontuacao;
                                  a mascara vale para ler, e nao so para digitar. */}
                              {c.telefone ? mascaraTelefone(c.telefone) : 'Telefone não informado'}
                            </dd>
                          </div>

                          <div className="flex items-center gap-1.5 min-w-0 sm:col-span-2">
                            <dt className="sr-only">E-mail</dt>
                            <div className="w-3.5 h-3.5 flex items-center justify-center text-foreground-400 shrink-0">
                              <i className="ri-mail-line text-[11px]"></i>
                            </div>
                            <dd className="text-xs text-foreground-600 truncate">
                              {c.email || 'E-mail não informado'}
                            </dd>
                          </div>
                        </dl>

                        {c.observacoes && (
                          <p className="text-xs text-foreground-500 mt-2 leading-relaxed border-t border-background-200/70 pt-2">
                            <span className="font-medium text-foreground-600">Observações: </span>
                            {c.observacoes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
